import { GeneralRecommendationResponse } from 'api-clients/monolith';
import { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';

import { BulbIcon } from '@/components/icons/bulb';
import { PlaneSendIcon } from '@/components/icons/plane-send';
import { Button } from '@/components/ui/button';
import { Typography } from '@/components/ui/typography';
import { useOpeningDetails } from '@/contexts/opening-context';
import { useRecommendationContext } from '@/contexts/recommendation-context';
import { useOpeningSlug } from '@/hooks/use-opening-slug';

import { useMessages } from './messages';

interface CardProps {
  channels: GeneralRecommendationResponse['recommended_channels'];
}

export function Card({ channels }: CardProps) {
  const openingSlug = useOpeningSlug();
  const { openRecommendationPostingDialog } = useRecommendationContext();
  const { getChannelRecommendationMessages } = useMessages();
  const { header, description } = getChannelRecommendationMessages();

  const { opening } = useOpeningDetails();
  const { id: openingId, title: openingTitle } = opening ?? {};

  const handlePostJobClick = useCallback(() => {
    if (!openingId || !openingTitle) return;

    openRecommendationPostingDialog({
      type: 'general',
      opening: {
        title: openingTitle,
        slug: openingSlug,
        externalId: openingId,
      },
      recommendedChannels: channels,
      // TODO: Add hiring goal
    });
  }, [
    channels,
    openRecommendationPostingDialog,
    openingId,
    openingSlug,
    openingTitle,
  ]);

  // If there is a single channel and one of INDEED, SPRING, BOOST, show the channel name and a button to post the job
  // Otherwise show the channels in a list and a button to post the job to all the recommended channels
  return (
    <div className="shadow-base flex w-[248px] flex-col items-start justify-center gap-2 rounded-lg bg-white p-4">
      <BulbIcon />
      <Typography variant="body2">{header}</Typography>
      <Typography variant="subtitle1">{description}</Typography>
      {channels.length === 1 ? (
        <Button onClick={handlePostJobClick} variant="secondary">
          <PlaneSendIcon />
          <FormattedMessage
            defaultMessage="Post Job to {channel}"
            values={{ channel: channels[0].name }}
            description="CTA when one board is recommended; {channel} is the board display name"
          />
        </Button>
      ) : (
        <>
          <div className="flex gap-1">
            <Typography variant="subtitle1">
              <FormattedMessage
                defaultMessage="Channels:"
                description="Label before comma-separated list of recommended board names"
              />
            </Typography>
            <Typography variant="subtitle2">
              {channels.map(channel => channel.name).join(', ')}
            </Typography>
          </div>
          <Button onClick={handlePostJobClick} variant="secondary">
            <PlaneSendIcon />
            <FormattedMessage
              defaultMessage="Post Job to channels"
              description="CTA to post this job to every recommended board at once"
            />
          </Button>
        </>
      )}
    </div>
  );
}
